import { createFileRoute, Link } from '@tanstack/react-router';
import { DesktopUsers } from '@/features/users/desktop/DesktopUsers';
import { MobileUsers } from '@/features/users/mobile/MobileUsers';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { Button, Badge, CloseButton, Group, Text, TextInput } from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { IconDownload, IconPlus, IconSearch, IconUpload, IconUsersGroup } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { useJsonApiTable } from '@/shared/components/DataTable/useJsonApiTable';
import { ColumnFilterInput } from '@/shared/components/DataTable/ColumnFilterInput';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import type { TableFilters, TableSort } from '@/shared/components/DataTable/types';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { fetchList } from '@/shared/api/jsonapi/client';
import { downloadCsv, type CsvColumn } from '@/shared/lib/exportCsv';
import type { FilterClause, ListQuery } from '@/shared/api/jsonapi/types';
import type { UserRow } from '@/features/users/api';

const DEFAULT_PAGE_SIZE = 25;
/** Sort di default (email ascendente): se attivo l'URL resta pulito. */
const DEFAULT_SORT = 'email';
/** Valore sentinella per "nessun ordinamento" (distinto dal default). */
const SORT_NONE = 'none';
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 2000;

/** Search params lista utenti: param assente = default (URL pulito). */
interface UsersSearch {
  /** 1-based; assente = pagina 1. */
  page?: number;
  /** Assente = 25. */
  pageSize?: number;
  /** Es. 'email' | '-lastName' | 'none'; assente = 'email' asc. */
  sort?: string;
  /** Ricerca globale (campo email). */
  q?: string;
  /** Filtri di colonna: { colonna: valore }. */
  filters?: Record<string, string>;
  /** Vista tabella completa (`table`); assente = tabella brand MaeDesign. */
  view?: 'table';
}

function parseFiltersParam(v: unknown): Record<string, string> | undefined {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string' && val !== '') out[k] = val;
  }
  return Object.keys(out).length ? out : undefined;
}

export const Route = createFileRoute('/_app/users/')({
  validateSearch: (s: Record<string, unknown>): UsersSearch => ({
    page: typeof s.page === 'number' && s.page > 1 ? Math.floor(s.page) : undefined,
    pageSize: typeof s.pageSize === 'number' && s.pageSize > 0 && s.pageSize !== DEFAULT_PAGE_SIZE ? Math.floor(s.pageSize) : undefined,
    sort: typeof s.sort === 'string' && s.sort && s.sort !== DEFAULT_SORT ? s.sort : undefined,
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    filters: parseFiltersParam(s.filters),
    view: s.view === 'table' ? 'table' : undefined,
  }),
  component: UsersPage,
});

/** Mobile = lista MaeDesign; desktop: default tabella brand, `?view=table` = tabella completa. */
function UsersPage() {
  const isMobile = useIsMobileShell();
  const { view } = Route.useSearch();
  if (isMobile) return <MobileUsers />;
  return view === 'table' ? <UsersListPage /> : <DesktopUsers />;
}

function sortParamToState(p?: string): TableSort | null {
  if (!p) return { field: DEFAULT_SORT, desc: false };
  if (p === SORT_NONE) return null;
  return { field: p.replace(/^-/, ''), desc: p.startsWith('-') };
}

function sortStateToParam(s: TableSort | null): string | undefined {
  if (!s) return SORT_NONE;
  const v = `${s.desc ? '-' : ''}${s.field}`;
  return v === DEFAULT_SORT ? undefined : v;
}

const USER_CSV_COLUMNS: Array<CsvColumn<UserRow>> = [
  { header: 'Email', accessor: (r) => r.email },
  { header: 'Nome', accessor: (r) => r.firstName },
  { header: 'Cognome', accessor: (r) => r.lastName },
  { header: 'Telefono', accessor: (r) => r.phoneNumber },
  { header: 'Confermato', accessor: (r) => (r.emailConfirmed == null ? '' : r.emailConfirmed ? 'Sì' : 'No') },
  { header: 'Bloccato', accessor: (r) => (r.lockoutEnabled == null ? '' : r.lockoutEnabled ? 'Sì' : 'No') },
];

const PAGE_SIZES = [10, 25, 50, 100];

function UsersListPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const [exporting, setExporting] = useState(false);

  // Stato tabella derivato dai search params (URL = fonte di verità).
  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE;
  const sort = useMemo(() => sortParamToState(search.sort), [search.sort]);
  const filters = useMemo<TableFilters>(() => search.filters ?? {}, [search.filters]);
  const globalFilter = search.q ?? '';

  const updateSearch = (patch: Partial<UsersSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const { data, isLoading, isError, error, refetch } = useJsonApiTable<UserRow>('users', {
    page: { page, pageSize },
    sort,
    filters,
    globalFilter,
    globalFilterFields: ['email'],
  });

  const changeFilter = (field: string, value: string) => {
    const next: Record<string, string> = { ...filters };
    if (value) next[field] = value; else delete next[field];
    updateSearch({ filters: Object.keys(next).length ? next : undefined, page: undefined });
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      // Ricostruisce la stessa query della tabella (sort+filtri correnti) e
      // scarica tutte le pagine fino al cap.
      const filter: Record<string, FilterClause> = {};
      for (const [field, value] of Object.entries(filters)) {
        if (value) filter[field] = { like: value };
      }
      if (globalFilter) filter.email = { like: globalFilter };

      const rows: UserRow[] = [];
      for (let p = 1; rows.length < EXPORT_MAX_ROWS; p++) {
        const query: ListQuery = {
          page: { number: p, size: EXPORT_PAGE_SIZE },
          sort: sort ? [`${sort.desc ? '-' : ''}${sort.field}`] : undefined,
          filter: Object.keys(filter).length ? filter : undefined,
        };
        const res = await fetchList<UserRow>('users', query);
        rows.push(...res.data);
        if (res.data.length < EXPORT_PAGE_SIZE) break;
      }
      const truncated = rows.length >= EXPORT_MAX_ROWS;
      downloadCsv(`utenti-${new Date().toISOString().slice(0, 10)}`, USER_CSV_COLUMNS, rows.slice(0, EXPORT_MAX_ROWS));
      notifications.show({
        color: truncated ? 'orange' : 'green',
        message: truncated
          ? `Esportate le prime ${EXPORT_MAX_ROWS} righe (il dataset è più grande: filtra per esportare il resto).`
          : `Esportate ${rows.length} righe.`,
      });
    } catch (e) {
      notifyError(e, 'Errore export');
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<DataTableColumn<UserRow>[]>(() => {
    const filtro = (accessor: string) => ({
      filter: () => <ColumnFilterInput value={filters[accessor] ?? ''} onCommit={(v) => changeFilter(accessor, v)} />,
      filtering: Boolean(filters[accessor]),
    });
    return [
      { accessor: 'email', title: 'Email', sortable: true, ...filtro('email') },
      { accessor: 'firstName', title: 'Nome', sortable: true, ...filtro('firstName') },
      { accessor: 'lastName', title: 'Cognome', sortable: true, ...filtro('lastName') },
      { accessor: 'phoneNumber', title: 'Telefono', sortable: false, ...filtro('phoneNumber') },
      {
        accessor: 'emailConfirmed',
        title: 'Confermato',
        width: 110,
        sortable: true,
        render: (row) => (row.emailConfirmed ? <Badge color="green">Sì</Badge> : <Badge color="yellow">No</Badge>),
      },
      {
        accessor: 'lockoutEnabled',
        title: 'Bloccato',
        width: 100,
        sortable: true,
        render: (row) => (row.lockoutEnabled ? <Badge color="red">Sì</Badge> : <Badge color="gray">No</Badge>),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    /* Vista tabella degli utenti: la vista predefinita (`DesktopUsers`) era già in
       MaeDesign, questa restava con la testata DS vecchia — stessa pagina, due identità. */
    <MaeShell>
      <MaePageHead
        title="Utenti"
        sub="Account, ruoli e permessi degli utenti dell’azienda."
        icon={<IconUsersGroup size={23} />}
        actions={
          <>
            <Button leftSection={<IconUpload size={16} />} variant="light" component={Link} to="/users/import">
              Importa Excel
            </Button>
            <Button leftSection={<IconPlus size={16} />} component={Link} to="/users/new">
              Nuovo utente
            </Button>
          </>
        }
      />
      {/* L'errore non è più una striscia dentro la toolbar della tabella: è l'avviso di casa,
          che dice cosa è andato storto e offre il riprova (regola 7). */}
      {isError && <QueryErrorAlert error={error} onRetry={() => void refetch()} />}
      <Group justify="space-between" wrap="wrap" gap="sm" mb="sm">
        <TextInput
          size="xs"
          w={260}
          placeholder="Cerca per email…"
          leftSection={<IconSearch size={14} />}
          value={globalFilter}
          onChange={(e) => updateSearch({ q: e.currentTarget.value || undefined, page: undefined })}
          rightSection={globalFilter ? <CloseButton size="sm" onClick={() => updateSearch({ q: undefined, page: undefined })} aria-label="Pulisci ricerca" /> : null}
        />
        <Button
          size="xs"
          variant="default"
          leftSection={<IconDownload size={14} />}
          loading={exporting}
          disabled={isLoading || (data?.total ?? 0) === 0}
          onClick={() => void exportCsv()}
        >
          Esporta CSV
        </Button>
      </Group>
      <DataTable<UserRow>
        withTableBorder
        borderRadius="lg"
        shadow="sm"
        highlightOnHover
        striped
        minHeight={(data?.data.length ?? 0) === 0 ? 220 : undefined}
        columns={columns}
        records={data?.data ?? []}
        fetching={isLoading}
        page={page}
        onPageChange={(p) => updateSearch({ page: p > 1 ? p : undefined })}
        totalRecords={data?.total ?? 0}
        recordsPerPage={pageSize}
        onRecordsPerPageChange={(n) => updateSearch({ pageSize: n !== DEFAULT_PAGE_SIZE ? n : undefined, page: undefined })}
        recordsPerPageOptions={PAGE_SIZES}
        recordsPerPageLabel="Righe per pagina"
        sortStatus={{ columnAccessor: sort?.field ?? '', direction: sort?.desc ? 'desc' : 'asc' }}
        onSortStatusChange={({ columnAccessor, direction }) =>
          updateSearch({ sort: sortStateToParam({ field: String(columnAccessor), desc: direction === 'desc' }), page: undefined })}
        onRowClick={({ record }) => void navigate({ to: `/users/${record.id}` })}
        /* Il vuoto e l'attesa non sono la stessa cosa (regola 7): finché si legge si mostra il
           caricamento, altrimenti si direbbe «nessun risultato» mentre la risposta è per strada. */
        emptyState={
          <div style={{ pointerEvents: 'auto' }}>
            {isLoading
              ? <InlineLoader label="Caricamento degli utenti…" minHeight={200} />
              : <Text c="dimmed" ta="center" py="xl">Nessun risultato</Text>}
          </div>
        }
      />
    </MaeShell>
  );
}
